<script setup>
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { api, upload, blobUrl, NetworkError } from '@/lib/http.js'
import { offline } from '@/lib/offline.js'
import { entityPath } from '@/lib/entities.js'
import { formatBytes, formatRelative } from '@/lib/format.js'
import { useUi } from '@/stores/ui.js'
import { reportError } from '@/composables/useSave.js'
import UiButton from '@/components/ui/UiButton.vue'
import UiModal from '@/components/ui/UiModal.vue'
import UiEmpty from '@/components/ui/UiEmpty.vue'
import { previewKind } from '@/lib/filePreview.js'
import FilePreview from './FilePreview.vue'

/**
 * Files on any record (D-45): uploaded in two steps (temporary, then attached), served only
 * through the record's permission check, previewed when PDF or image (D-104), films and
 * recordings played as they stream (D-143), text files and spreadsheets shown too (D-223).
 * Offline, files the device keeps (addictive-offline attachments budget) can still be opened.
 * Given `languages` (a sales material's, D-332), each file is for one of them or (none) for all.
 * Given `kind`, only files of that kind, and new ones are of it (an offer's documents from the customer, D-358).
 */
const props = defineProps({ entity: { type: String, required: true }, record: { type: Object, required: true }, languages: { type: Array, default: () => [] }, kind: { type: String, default: '' } })
// The language new files are for (none — all).
const language = ref('')
const { t } = useI18n()
const ui = useUi()
const files = ref([])
const loading = ref(false)
const uploading = ref([])
const preview = ref(null)
const dragOver = ref(false)
const offlineOnly = ref(false)
const input = ref(null)

async function load() {
  loading.value = true
  try {
    files.value = (await api(`/${entityPath(props.entity)}/${props.record.uuid}/attachments`)).data.filter((f) => !props.kind || f.kind === props.kind)
    offlineOnly.value = false
  } catch (e) {
    if (!(e instanceof NetworkError)) return reportError(e)
    offlineOnly.value = true
    const cached = await offline.db.table('files').where('attachable').equals(`${props.entity}:${props.record.uuid}`).toArray()
    files.value = cached.map((f) => ({ ...f, cached: true })).filter((f) => !props.kind || f.kind === props.kind)
  } finally {
    loading.value = false
  }
}
onMounted(load)

async function add(list) {
  for (const file of list) {
    uploading.value.push(file.name)
    try {
      const temp = (await upload('/uploads/temp', file)).data
      await api(`/${entityPath(props.entity)}/${props.record.uuid}/attachments`, { method: 'POST', body: { upload_uuid: temp.uuid, ...(props.kind ? { kind: props.kind } : {}), ...(props.languages.length ? { language: language.value || null } : {}) } })
    } catch (e) {
      if (e instanceof NetworkError) ui.warn(t('messages.onlineOnly'))
      else ui.error(`${t('messages.uploadFailed')} ${file.name}: ${e.message}`)
    } finally {
      uploading.value = uploading.value.filter((n) => n !== file.name)
    }
  }
  load()
}
function onDrop(e) {
  dragOver.value = false
  if (props.record.abilities?.link_attachment) add([...e.dataTransfer.files])
}

async function fileUrl(file) {
  if (file.cached || offlineOnly.value) {
    const cached = await offline.cachedFile(file.url)
    if (cached) return cached
  }
  return blobUrl(file.url)
}
const kindOf = (file) => previewKind({ mime: file.mime, name: file.name })
async function open(file) {
  const kind = kindOf(file)
  // A film or a recording plays as Core streams it, not after the whole file is fetched.
  if (['video', 'audio'].includes(kind) && !file.cached && !offlineOnly.value) {
    preview.value = { file, kind, url: file.url.replace(/^https?:\/\/[^/]+/, ''), media: true }
    return
  }
  try {
    const url = await fileUrl(file)
    if (kind) preview.value = { file, kind, url }
    else download(file, url)
  } catch (e) {
    reportError(e)
  }
}
function download(file, url) {
  const a = document.createElement('a')
  a.href = url
  a.download = file.name
  a.click()
}
async function remove(file) {
  const ok = await ui.ask({ title: t('common.delete'), message: file.name, danger: true, confirmLabel: t('common.delete') })
  if (!ok) return
  try {
    await api(`/attachments/${file.uuid}`, { method: 'DELETE' })
    load()
  } catch (e) {
    reportError(e)
  }
}
async function setLanguage(file, value) {
  try {
    const saved = (await api(`/attachments/${file.uuid}`, { method: 'PATCH', body: { language: value || null } })).data
    files.value = files.value.map((f) => (f.uuid === saved.uuid ? saved : f))
  } catch (e) {
    reportError(e)
  }
}
function closePreview() {
  if (preview.value && !preview.value.media) URL.revokeObjectURL(preview.value.url)
  preview.value = null
}
const icon = (mime) => (mime?.includes('pdf') ? 'fa-file-pdf text-red-500' : mime?.startsWith('image/') ? 'fa-file-image text-sky-500' : mime?.includes('sheet') || mime?.includes('excel') || mime === 'text/csv' ? 'fa-file-excel text-emerald-600' : mime?.includes('word') ? 'fa-file-word text-blue-600' : mime?.includes('presentation') || mime?.includes('powerpoint') ? 'fa-file-powerpoint text-orange-600' : mime?.startsWith('text/') ? 'fa-file-lines text-slate-500' : 'fa-file text-faint')
</script>

<template>
  <section class="space-y-3">
    <label v-if="record.abilities?.link_attachment && languages.length" class="flex items-center gap-2 text-sm text-muted">{{ $t('attachments.newFilesFor') }}
      <select v-model="language" class="input w-auto py-1 text-sm" data-testid="attachment-language"><option value="">{{ $t('attachments.allLanguages') }}</option><option v-for="l in languages" :key="l" :value="l">{{ l.toUpperCase() }}</option></select>
    </label>
    <div
      v-if="record.abilities?.link_attachment"
      :class="['flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed px-4 py-6 text-sm text-muted transition-colors', dragOver ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30' : 'border-default hover:border-brand-300']"
      role="button"
      tabindex="0"
      @click="input.click()"
      @keydown.enter="input.click()"
      @dragover.prevent="dragOver = true"
      @dragleave="dragOver = false"
      @drop.prevent="onDrop"
    >
      <i class="fa-solid fa-cloud-arrow-up text-2xl" aria-hidden="true"></i>
      <span>{{ $t('attachments.drop') }}</span>
      <input ref="input" type="file" multiple class="hidden" data-testid="attachment-input" @change="add([...$event.target.files]); $event.target.value = ''" />
    </div>
    <p v-for="name in uploading" :key="name" class="text-sm text-muted"><i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i> {{ $t('attachments.uploading', { name }) }}</p>

    <UiEmpty v-if="!loading && !files.length" icon="fa-paperclip" :title="$t('attachments.empty')" />
    <ul v-else class="card divide-y divide-[var(--border)]" data-testid="attachments">
      <li v-for="f in files" :key="f.uuid" class="flex items-center gap-3 px-3 py-2">
        <i :class="['fa-solid text-xl', icon(f.mime)]" aria-hidden="true"></i>
        <button type="button" class="min-w-0 flex-1 text-left" @click="open(f)">
          <p class="truncate text-sm font-medium hover:underline">{{ f.name }}</p>
          <p class="text-xs text-faint">{{ formatBytes(f.size) }}<template v-if="f.uploaded_by"> · {{ $t('attachments.uploadedBy', { who: f.uploaded_by.label, when: formatRelative(f.created_at) }) }}</template><template v-if="f.cached"> · {{ $t('attachments.offlineCached') }}</template></p>
        </button>
        <select v-if="languages.length && record.abilities?.link_attachment && !offlineOnly" :value="f.language || ''" class="input w-auto py-0.5 text-xs" :aria-label="$t('attachments.fileLanguage')" data-testid="attachment-file-language" @change="setLanguage(f, $event.target.value)">
          <option value="">{{ $t('attachments.allLanguages') }}</option><option v-for="l in languages" :key="l" :value="l">{{ l.toUpperCase() }}</option>
        </select>
        <span v-else-if="languages.length && f.language" class="text-xs uppercase text-muted">{{ f.language }}</span>
        <UiButton size="xs" variant="ghost" icon="fa-download" :aria-label="$t('common.download')" @click="fileUrl(f).then((u) => download(f, u)).catch(reportError)" />
        <UiButton v-if="record.abilities?.link_attachment && !offlineOnly" size="xs" variant="ghost" icon="fa-trash-can" :aria-label="$t('common.delete')" @click="remove(f)" />
      </li>
    </ul>

    <UiModal :open="!!preview" :title="preview?.file.name || ''" size="full" @close="closePreview">
      <FilePreview v-if="preview" :file="preview.file" :kind="preview.kind" :src="preview.url" />
      <template #footer>
        <UiButton v-if="preview?.media" icon="fa-download" @click="fileUrl(preview.file).then((u) => download(preview.file, u)).catch(reportError)">{{ $t('common.download') }}</UiButton>
        <UiButton v-else icon="fa-download" @click="download(preview.file, preview.url)">{{ $t('common.download') }}</UiButton>
      </template>
    </UiModal>
  </section>
</template>
